/**
 * Small utilities shared by `SurfaceBoxHandler` and `SurfacePolyHandler`.
 *
 * These were inlined as private helpers in each handler before; pulling
 * them out keeps the two handlers' snap / pointer plumbing identical
 * without forcing the rest of the modal logic (which differs significantly
 * between rectangle-footprint and polyline-drawing flows) into a shared
 * abstraction. The deeper consolidation lives in
 * `SurfaceFootprintGizmo` / the polymorphic `surfaceTool` slot — these
 * helpers are the leaf-level commonalities that would otherwise drift.
 */

import type * as THREE from "three/webgpu";
import type { FaceBasis } from "../modeling/faceBasis.ts";
import { getDynamicSnapSpacing } from "../modeling/snap.ts";
import { basisGridUV, gridUvToWorld } from "../modeling/surfaceBoxMath.ts";

/** True iff Shift is currently held (covers both `shiftKey` and modifier-state queries). */
export function pointerShiftActive(e: PointerEvent): boolean {
  return e.shiftKey || e.getModifierState?.("Shift") === true;
}

/** Convert a viewport client-space coordinate to NDC (-1..1, +Y up). */
export function ndcFromClient(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { ndcX: number; ndcY: number } {
  return {
    ndcX: ((clientX - rect.left) / rect.width) * 2 - 1,
    ndcY: -((clientY - rect.top) / rect.height) * 2 + 1,
  };
}

/**
 * Snap a world-space point onto the basis grid spaced at `spacing`. Used
 * by the pure-grid-snap fallback in both Box and Poly when the snap
 * raycast misses everything else.
 */
export function snapPointToSurfaceGrid(
  basis: FaceBasis,
  worldPoint: readonly [number, number, number],
  spacing: number,
): [number, number, number] {
  const uv = basisGridUV(basis, [worldPoint[0], worldPoint[1], worldPoint[2]]);
  return gridUvToWorld(
    basis,
    Math.round(uv.u / spacing) * spacing,
    Math.round(uv.v / spacing) * spacing,
  );
}

/**
 * Snap-then-grid-then-plane spacing helper. Mirrors the call signature
 * the handlers use today — a thin wrapper so the constant `0` for
 * `getDynamicSnapSpacing`'s offset only lives in one place.
 */
export function dynamicSurfaceGridSpacing(gridLogDist: number): number {
  return getDynamicSnapSpacing(gridLogDist, 0);
}

// ---------------------------------------------------------------------------
// World ↔ screen helpers (used by Surface Poly's endpoint hit testing).
// ---------------------------------------------------------------------------

/**
 * Project a world-space point to screen pixel coordinates relative to the
 * canvas's bounding rect. Reuses a module-level scratch Vector3 so this
 * stays allocation-free in the rAF loop.
 */
export function worldToScreenPx(
  scratchProj: THREE.Vector3,
  world: readonly [number, number, number],
  camera: THREE.Camera,
  rect: DOMRect,
): { x: number; y: number } {
  scratchProj.set(world[0], world[1], world[2]).project(camera);
  return {
    x: ((scratchProj.x + 1) / 2) * rect.width,
    y: ((1 - scratchProj.y) / 2) * rect.height,
  };
}

/**
 * True iff the device's primary pointer is "coarse" (touch / pen). Used
 * to widen click targets when not using a mouse.
 */
export function isCoarsePointer(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches;
}
